import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PrimaryStepper from "../../../components/_utils/CommonSteppers/PrimaryStepper";
import TextAuthMatrixSelectTxnAmount from "../../../components/Settings/TestAuthMatrix/TestAuthMatrixSelectTxnAmount";
import TextAuthMatrixSelectUser from "../../../components/Settings/TestAuthMatrix/TestAuthMatrixSelectUser";

import {
  getTxnAuth,
  testAuthMatrix,
  testPredictedApproval,
} from "../../../redux/features/Settings/TestAuthMatrix";
import "./index.css";
import SVGComponent from "../../../components/_utils/SVGComponent";
import ICONS from "../../../assets/Icons";
import CommonToast from "../../../components/_utils/CommonToast";

const TestAuthMatrix = () => {
  //variables -------
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loginData = useSelector((state) => state.refreshToken.data);
  const steps = [
    "Select transaction type and enter the amount",
    "Select users to approve",
    "Select previous approvers (Optional)",
  ];

  //states -------
  const [activeStep, setActiveStep] = useState(0);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const [showRupeeSymbol, setShowRupeeSymbol] = useState(false);
  const [amount, setAmount] = useState("");
  const [amountInWords, setAmountInWords] = useState("");
  const [txnType, setTxnType] = useState("");
  const [testPredictedAppLoading, setTestPredictedAppLoading] = useState(false);
  const [testAuthMatrixLoading, setTestAuthMatrixLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState([{}]);
  const [selectedPreviousUsersId, setSelectedPreviousUsersId] = useState([]);
  const [selectedPreviousUsers, setSelectedPreviousUsers] = useState([]);

  //functions --------
  const handleTestPredictedApproval = async (page) => {
    setTestPredictedAppLoading(true);
    const entity_id = loginData?.status ? loginData?.entity?.entity_id : "";
    let grade_count = {};

    selectedPreviousUsers?.forEach((val) => {
      const grade = val?.grade?.trim();
      if (grade) {
        grade_count[grade] = grade_count[grade] ? grade_count[grade] + 1 : 1;
      }
    });
    const existing_approvals_grade_count =
      page === "outcome" ? JSON.stringify(grade_count) : "{}";

    let response = await dispatch(
      testPredictedApproval({
        entity_id,
        txnType,
        amount,
        existing_approvals_grade_count,
      }),
    );

    if (page === "outcome") {
      if (response?.payload?.status) {
        navigate(
          "/settings/test_authorization_matrix/test_authorization_matrix_success",
          {
            state: {
              page: "partial",
              selectedUser: selectedUser?.[0],
              selectedPreviousUsers: selectedPreviousUsers,
              predicted_approval: response?.payload?.predicted_approval,
            },
          },
        );
      } else {
        if (response?.payload?.errors?.transaction_status) {
          CommonToast({
            text:
              "Transaction status is " +
              response?.payload?.errors?.transaction_status,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        } else if (
          response?.payload?.errors?.reason &&
          response?.payload?.errors?.error
        ) {
          CommonToast({
            text: response?.payload?.errors?.reason,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      }
    } else {
      if (response?.payload?.status) {
        setActiveStep(1);
      } else {
        CommonToast({
          text: "No rules found for the provided data.",
          icon: ICONS?.ERROR_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }
    }

    setTestPredictedAppLoading(false);
  };

  const handleTestAuthMatrix = async () => {
    setTestAuthMatrixLoading(true);
    const entity_id = loginData?.status ? loginData?.entity?.entity_id : "";
    const user_id = selectedUser?.[0]?.user_id;
    const user_grade = selectedUser?.[0]?.grade;
    let grade_count = {};

    selectedPreviousUsers?.forEach((val) => {
      const grade = val?.grade?.trim();
      if (grade) {
        grade_count[grade] = grade_count[grade] ? grade_count[grade] + 1 : 1;
      }
    });

    const existing_approvals = {
      grade_count: grade_count,
      user_ids: selectedPreviousUsersId,
    };

    let response = await dispatch(
      testAuthMatrix({
        entity_id,
        txnType,
        user_id,
        user_grade,
        amount,
        existing_approvals,
      }),
    );

    if (response?.payload?.status) {
      if (
        response?.payload?.transaction_auth_matrix
          ?.transaction_final_approved === true
      ) {
        const id =
          response?.payload?.transaction_auth_matrix?.authorization_rule_id;
        const res = await dispatch(getTxnAuth({ id }));

        if (res?.payload?.status) {
          navigate(
            "/settings/test_authorization_matrix/test_authorization_matrix_success",
            {
              state: {
                page: "success",
                selectedUser: selectedUser?.[0],
                selectedPreviousUsers: selectedPreviousUsers,
                transaction_auth_matrices:
                  res?.payload?.transaction_auth_matrices,
              },
            },
          );
        }
      } else if (
        response?.payload?.transaction_auth_matrix
          ?.transaction_final_approved === false
      ) {
        handleTestPredictedApproval("outcome");
      }
    } else {
      navigate(
        "/settings/test_authorization_matrix/test_authorization_matrix_error",
        {
          state: {
            page: "error",
            selectedUser: selectedUser?.[0],
            selectedPreviousUsers: selectedPreviousUsers,
          },
        },
      );
    }
    setTestAuthMatrixLoading(false);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate("/settings");
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      handleTestPredictedApproval();
    } else if (activeStep === 1) {
      setActiveStep((prev) => prev + 1);
    } else if (activeStep === 2) {
      handleTestAuthMatrix();
    }
  };

  //useEffects --------
  useEffect(() => {
    if (activeStep === 0 && txnType !== "" && amount !== "" && amount !== "0") {
      setNextButtonEnabled(true);
    } else if (
      (activeStep === 1 && selectedUser?.[0]?.id) ||
      activeStep === 2
    ) {
      setNextButtonEnabled(true);
    } else {
      setNextButtonEnabled(false);
    }
  }, [
    activeStep,
    txnType,
    amount,
    selectedUser,
    selectedPreviousUsersId,
    selectedPreviousUsers,
  ]);

  // stepper config
  const commonStepperConfig = {
    previousEnableBgColor: "var(--primary-nav-act)",
    previousDisableBgColor: "var(--primary-nav-act)",
    previousEnableTextColor: "var(--text-primary)",
    nextEnableBgColor: "var(--text-primary)",
    nextDisableBgColor: "var(--text-disabled)",
    nextEnableTextColor: "var(--primary-bg)",
    nextDisableTextColor: "var(--text-primary)",
    previousHoverOption: "white",
    nextHoverOption: "black",
  };

  const dataArray = steps.map((stepLabel, index) => ({
    ...commonStepperConfig,
    previousText: index === 0 ? "Cancel" : "Back",
    nextText: index === 0 ? "Fetch" : index === 1 ? "Next" : "Test",
    nextLoaderNeeded: index === 0 || index === 2,
    nextIsLoading:
      index === 0
        ? testPredictedAppLoading
        : index === 2
          ? testAuthMatrixLoading
          : false,
    nextIsEnabled: nextButtonEnabled,
    stepperText: stepLabel,
  }));

  return (
    <>
      <div className="w-full-width h-full-height bg-primary-bg overflow-y-hidden flex flex-col">
        <div className="hidden Qsmd:flex min-h-navbar-breadcrumb-height Qlg:bg-transparent bg-primary-nav"></div>
        <div className="test-auth-stepper-main-div w-width-full z-z-index-header relative min-h-stepper-height">
          <div className="w-width-full Qlg:w-max-content-width m-auto">
            <PrimaryStepper
              activeStep={activeStep}
              handleForwardAction={handleNext}
              handleBackwardAction={handleBack}
              dataArray={dataArray}
            />
          </div>
        </div>
        <div className="test-auth-pages-container-div flex-1 ">
          <div className="test-auth-resp-header shadow-box-shadow-bottom">
            <div
              className="test-auth-resp-header-img"
              onClick={() => {
                navigate("/settings");
              }}
            >
              <SVGComponent
                url={ICONS?.ARROW_LEFT_WHITE}
                height={"var(--icon-sm)"}
                width={"var(--icon-sm)"}
                color={"var(--text-primary)"}
              />
            </div>
            <div className="test-auth-resp-header-text">
              <span className="text-base text-text-primary">
                Test authorization matrix
              </span>
              <span className="text-xs text-text-secondary font-semibold">
                {steps[activeStep]}
              </span>
            </div>
          </div>
          <div className="test-auth-pages-main-div">
            {activeStep === 0 ? (
              <TextAuthMatrixSelectTxnAmount
                showRupeeSymbol={showRupeeSymbol}
                setShowRupeeSymbol={setShowRupeeSymbol}
                amount={amount}
                setAmount={setAmount}
                txnType={txnType}
                setTxnType={setTxnType}
                amountInWords={amountInWords}
                setAmountInWords={setAmountInWords}
              />
            ) : activeStep === 1 ? (
              <TextAuthMatrixSelectUser
                pageType={"users"}
                activeStep={activeStep}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                setSelectedPreviousUsers={setSelectedPreviousUsers}
              />
            ) : (
              <TextAuthMatrixSelectUser
                hMatrixSelectUser
                pageType={"previousUsers"}
                activeStep={activeStep}
                selectedUser={selectedUser}
                selectedPreviousUsersId={selectedPreviousUsersId}
                setSelectedPreviousUsersId={setSelectedPreviousUsersId}
                selectedPreviousUsers={selectedPreviousUsers}
                setSelectedPreviousUsers={setSelectedPreviousUsers}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TestAuthMatrix;
